<template>
  <section>
    <Container>
      <HeadingLarge>
        Billing Summary
      </HeadingLarge>
    </Container>
    <div :class="$style.body">
      <Card>
        <Grid
          v-for="dataPoint in billingDataPoints"
          :key="dataPoint.name"
          :class="$style.dataPoint"
          :data-test-id="`billing-data-point__${dataPoint.name}`"
        >
          <GridItem :gridSpan="9">
            <HeadingSmall>
              {{ dataPoint.title }}
            </HeadingSmall>
            <ParagraphBody
              v-if="dataPoint.description"
            >
              {{ dataPoint.description }}
              <Link
                v-if="dataPoint.link"
                :href="dataPoint.link.href"
                :isExternal="true"
                target="_blank"
                :text="dataPoint.link.text"
              />
            </ParagraphBody>
          </GridItem>
          <GridItem :gridSpan="3">
            <HeadingSmall :class="$style.dataPointValue">
              {{ dataPoint.value }}
            </HeadingSmall>
          </GridItem>
        </Grid>
      </Card>

      <div
        v-if="monthlyBillingEstimateDataPoint.value"
        :class="$style.monthlyBillingEstimateWrapper"
      >
        <Card
          type="warning"
        >
          <Grid
            :class="$style.dataPoint"
            :data-test-id="`billing-data-point__${monthlyBillingEstimateDataPoint.name}`"
          >
            <GridItem :gridSpan="9">
              <HeadingSmall>
                {{ monthlyBillingEstimateDataPoint.title }}
              </HeadingSmall>
              <ParagraphBody>
                {{ monthlyBillingEstimateDataPoint.description }}
              </ParagraphBody>
            </GridItem>
            <GridItem :gridSpan="3">
              <HeadingSmall :class="$style.dataPointValue">
                {{ monthlyBillingEstimateDataPoint.value }}
              </HeadingSmall>
            </GridItem>
          </Grid>
        </Card>
      </div>

      <ParagraphBody>
        Have questions? Please contact
        <Link
          :href="`mailto:${$options.accountEmail}`"
          :text="$options.accountEmail"
        />
      </ParagraphBody>
    </div>
  </section>
</template>

<script>
import {
  Card,
  Container,
  Grid,
  GridItem,
  HeadingLarge,
  HeadingSmall,
  Link,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { accountEmail } from 'client/util/Constants/FeatureTrialConstants';
import { mapGetters, mapState } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import Util from 'client/util/Util';

const planModule = 'PlanModule';

export default {
  name: 'BillingSummary',

  components: {
    Card,
    Container,
    Grid,
    GridItem,
    HeadingLarge,
    HeadingSmall,
    Link,
    ParagraphBody,
  },

  accountEmail,
  EventHelper,

  computed: {
    ...mapState(planModule, [
      'plan',
    ]),

    ...mapGetters(planModule, [
      'hasAnnualContractType',
    ]),

    billingDataPoints() {
      const billingDataPoints = [
        {
          name: 'pricePerUser',
          title: this.plan.name,
          value: this.plan.pricePerUser ? `${Util.formatAsPrice(this.plan.pricePerUser)}/user` : '',
        },
        {
          name: 'contractType',
          title: 'Contract Type',
          value: this.plan.contractType,
        },
        {
          name: 'committedUsers',
          title: 'Committed Users',
          description: 'Committed users is the number of users originally committed to in your contract.',
          value: this.plan.committedUsers,
        },
        {
          name: 'additionalUsers',
          title: 'Additional Users',
          description: 'Additional users is the highest count of users in your directory that exceed your contracted minimum commitment in a single billing period. The charges for these users are billed monthly on a separate invoice.',
          value: this.plan.additionalUsers,
        },
        {
          name: 'highWaterMark',
          title: 'High Water Mark',
          description: 'High water mark is the highest count of users in your directory in a single monthly billing period. View',
          link: { href: 'https://www.jumpcloud.com/legal', text: 'Terms and Conditions.' },
          value: this.plan.highWaterMark,
        },
      ];

      // exclude data points with empty values
      return billingDataPoints.filter(dataPoint => dataPoint.value);
    },

    monthlyBillingEstimateDataPoint() {
      const estimate = this.hasAnnualContractType
        ? this.plan.uncommitedBillingEstimate : this.plan.totalBillingEstimate;
      return {
        name: 'monthlybillingEstimate',
        title: 'Monthly Billing Estimate',
        description: 'Estimate of your upcoming bill based on the current state of your account and the contract you have in place with JumpCloud.',
        value: estimate && `$${estimate}`,
      };
    },
  },
};
</script>

<style module>
.body {
  padding: var(--jc-spacer-medium) 0;
}

.dataPoint:not(:first-child) {
  padding-top: var(--jc-spacer);
}

.dataPoint:not(:last-child) {
  border-bottom: var(--jc-border);
  padding-bottom: var(--jc-spacer);
}

.dataPointValue {
  text-align: right;
}

.monthlyBillingEstimateWrapper {
  margin: var(--jc-spacer-medium) 0;
}
</style>
